import { supabase } from '@/lib/supabase';
import type {
  System,
  SystemLicensePlan,
  OrgSystemSubscription,
  OrgEffectiveLimitRow,
  OrgUsageRow,
  OrgLimitSnapshot,
} from '@/types';
import { LIMIT_KEYS, type LimitKey } from '@/types/licensing';

/** Thrown when cross-org limit reads fail — requires is_platform_admin()/is_super_admin(). */
export class PlatformAdminRequiredError extends Error {
  readonly code = 'PLATFORM_ADMIN_REQUIRED' as const;
  constructor(message = 'Platform admin access required to view organization limits') {
    super(message);
    this.name = 'PlatformAdminRequiredError';
  }
}

export function isPlatformAdminRequiredError(err: unknown): boolean {
  if (err instanceof PlatformAdminRequiredError) return true;
  if (err && typeof err === 'object' && 'code' in err && (err as { code?: string }).code === 'PLATFORM_ADMIN_REQUIRED') {
    return true;
  }
  const msg = err && typeof err === 'object' && 'message' in err
    ? String((err as { message?: unknown }).message ?? '')
    : String(err ?? '');
  return /permission denied|row-level security|PLATFORM_ADMIN_REQUIRED/i.test(msg);
}

function rethrowAsPlatformAdminIfNeeded(error: { message?: string; code?: string }): never {
  const msg = error.message ?? '';
  const code = error.code ?? '';
  // PostgREST / Postgres RLS denial surfaces as 42501 or permission-denied messages.
  if (
    code === '42501' ||
    code === 'PGRST301' ||
    /permission denied|row-level security|not authorized/i.test(msg)
  ) {
    throw new PlatformAdminRequiredError();
  }
  throw error;
}

function emptyLimitSnapshot(): OrgLimitSnapshot {
  return {
    used: 0,
    plan: 0,
    addon: 0,
    effective: 0,
  };
}

function mergeEffectiveLimits(
  limits: OrgEffectiveLimitRow[] | null | undefined,
  usage: OrgUsageRow[] | null | undefined,
): Record<LimitKey, OrgLimitSnapshot> {
  const snapshots = Object.fromEntries(
    LIMIT_KEYS.map((key) => [key, emptyLimitSnapshot()])
  ) as Record<LimitKey, OrgLimitSnapshot>;

  for (const row of limits ?? []) {
    snapshots[row.limit_key] = {
      used: snapshots[row.limit_key]?.used ?? 0,
      plan: Number(row.plan_value ?? 0),
      addon: Number(row.addon_value ?? 0),
      effective: Number(row.effective_value ?? 0),
    };
  }

  for (const row of usage ?? []) {
    snapshots[row.limit_key] = {
      ...(snapshots[row.limit_key] ?? emptyLimitSnapshot()),
      used: Number(row.used ?? 0),
    };
  }

  return snapshots;
}

export interface UpdateSubscriptionFieldsInput {
  planCode?: string | null;
  status?: 'active' | 'trial' | 'suspended' | 'cancelled' | null;
  effectivePriceAgorot?: number | null;
  overrideReason?: string | null;
  expiresAt?: string | null;
}

export const licensingApi = {
  updateSubscriptionFields: async (
    subscriptionId: string,
    input: UpdateSubscriptionFieldsInput,
  ): Promise<void> => {
    const { error } = await supabase.rpc('set_org_subscription_fields', {
      p_subscription_id: subscriptionId,
      p_plan_code: input.planCode ?? null,
      p_status: input.status ?? null,
      p_effective_price_agorot: input.effectivePriceAgorot ?? null,
      p_override_reason: input.overrideReason ?? null,
      p_expires_at: input.expiresAt ?? null,
    });
    if (error) throw error;
  },

  // Systems
  listSystems: async (): Promise<System[]> => {
    const { data, error } = await supabase
      .from('systems')
      .select('*')
      .order('sort_order');
    if (error) throw error;
    return data as System[];
  },

  // License plans for a system
  listPlans: async (systemCode: string, includeInactive = false): Promise<SystemLicensePlan[]> => {
    let q = supabase
      .from('system_license_plans')
      .select('*')
      .eq('system_code', systemCode)
      .order('sort_order');
    if (!includeInactive) q = q.eq('is_active', true);
    const { data, error } = await q;
    if (error) throw error;
    return data as SystemLicensePlan[];
  },

  // All plans (for overview)
  listAllPlans: async (includeInactive = false): Promise<SystemLicensePlan[]> => {
    let q = supabase.from('system_license_plans').select('*').order('system_code, sort_order');
    if (!includeInactive) q = q.eq('is_active', true);
    const { data, error } = await q;
    if (error) throw error;
    return data as SystemLicensePlan[];
  },

  // Update a plan
  updatePlan: async (id: string, updates: Partial<SystemLicensePlan>): Promise<SystemLicensePlan> => {
    const { data, error } = await supabase
      .from('system_license_plans')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data as SystemLicensePlan;
  },

  // Delete a plan
  deletePlan: async (id: string): Promise<void> => {
    const { error } = await supabase
      .from('system_license_plans')
      .delete()
      .eq('id', id);
    if (error) throw error;
  },

  // Create a plan
  createPlan: async (plan: Omit<SystemLicensePlan, 'id' | 'created_at'>): Promise<SystemLicensePlan> => {
    const { data, error } = await supabase
      .from('system_license_plans')
      .insert(plan)
      .select()
      .single();
    if (error) throw error;
    return data as SystemLicensePlan;
  },

  // Active subscriptions per system (with org names)
  listSubscriptions: async (systemCode?: string): Promise<(OrgSystemSubscription & { organizations: { name: string } | null })[]> => {
    let query = supabase
      .from('org_system_subscriptions')
      .select('*, organizations(name)')
      .order('created_at', { ascending: false });
    if (systemCode) query = query.eq('system_code', systemCode);
    const { data, error } = await query;
    if (error) throw error;
    return data as any[];
  },

  /**
   * Subscriptions for one org: legacy `org_system_subscriptions` plus `system_subscriptions`
   * (Jumpstart project, `supabase`) for jumpstart-chat / whatsapp.
   * When both exist for the same `system_code`, the catalog row wins so JumpStart Chat
   * assignments from `/jumpstart-chat` appear here (confirm source-of-truth rules with Cowork).
   */
  getOrgSubscriptions: async (orgId: string) => {
    // org_system_subscriptions has no FK to system_license_plans, so plans
    // are fetched separately and merged client-side. Include both active
    // and trial rows across all systems the org has.
    const { data: rawOrgRows, error: orgErr } = await supabase
      .from('org_system_subscriptions')
      .select('*, systems(name, code)')
      .eq('organization_id', orgId)
      .in('status', ['active', 'trial'])
      .order('created_at', { ascending: false });
    if (orgErr) throw orgErr;

    const { data: planRows, error: planErr } = await supabase
      .from('system_license_plans')
      .select('system_code, code, name, features, limits, price_monthly_agorot, price_yearly_agorot');
    if (planErr) throw planErr;

    const planIndex = new Map(
      (planRows ?? []).map((p: any) => [`${p.system_code}::${p.code}`, p]),
    );
    const orgRows = (rawOrgRows ?? []).map((r: any) => ({
      ...r,
      system_license_plans: planIndex.get(`${r.system_code}::${r.plan_code}`) ?? null,
    }));

    const { data: catalogRows, error: catErr } = await supabase
      .from('system_subscriptions')
      .select(
        `
        id,
        org_id,
        system_code,
        plan_code,
        status,
        billing_cycle_months,
        current_period_start,
        current_period_end,
        created_at,
        updated_at,
        system_license_plans (
          name,
          code,
          features,
          limits,
          price_monthly_agorot,
          price_yearly_agorot
        )
      `
      )
      .eq('org_id', orgId)
      .in('system_code', ['jumpstart-chat', 'whatsapp']);

    if (catErr) {
      console.warn('[licensing] system_subscriptions:', catErr.message);
    }

    const catalogList = (catalogRows ?? []) as unknown as Array<{
      id: string;
      org_id: string;
      system_code: string;
      plan_code: string;
      status: string;
      billing_cycle_months: number | null;
      current_period_start: string;
      current_period_end: string | null;
      created_at: string;
      updated_at: string;
      system_license_plans: {
        name: string;
        code: string;
        features: Record<string, unknown> | null;
        limits: Record<string, unknown> | null;
        price_monthly_agorot: number;
        price_yearly_agorot: number;
      } | null;
    }>;

    const catalogSystemCodes = new Set(
      catalogList.map((r) => r.system_code).filter((c) => c === 'jumpstart-chat' || c === 'whatsapp')
    );

    const filteredOrg = (orgRows ?? []).filter((r) => !catalogSystemCodes.has(r.system_code));

    const mappedCatalog: (OrgSystemSubscription & {
      systems: { name: string; code: string } | null;
      system_license_plans: {
        name: string;
        code: string;
        features?: Record<string, unknown> | null;
        limits?: Record<string, unknown> | null;
        price_monthly_agorot: number;
        price_yearly_agorot: number;
      } | null;
    })[] = catalogList.map((row) => {
      const months = row.billing_cycle_months ?? 1;
      const billing_cycle: OrgSystemSubscription['billing_cycle'] =
        months >= 12 ? 'yearly' : months >= 1 ? 'monthly' : 'none';
      const sysName =
        row.system_code === 'jumpstart-chat'
          ? 'JumpStart Chat'
          : row.system_code === 'whatsapp'
            ? 'WhatsApp'
            : row.system_code;
      return {
        id: row.id,
        organization_id: row.org_id,
        system_code: row.system_code,
        plan_code: row.plan_code,
        status: row.status as OrgSystemSubscription['status'],
        billing_cycle,
        price_agorot: 0,
        effective_price_agorot: null,
        override_reason: null,
        started_at: row.current_period_start,
        expires_at: row.current_period_end,
        notes: null,
        metadata: {},
        created_at: row.created_at,
        updated_at: row.updated_at,
        systems: { code: row.system_code, name: sysName },
        system_license_plans: row.system_license_plans
          ? {
              name: row.system_license_plans.name,
              code: row.system_license_plans.code,
              features: row.system_license_plans.features ?? undefined,
              limits: row.system_license_plans.limits ?? undefined,
              price_monthly_agorot: row.system_license_plans.price_monthly_agorot,
              price_yearly_agorot: row.system_license_plans.price_yearly_agorot,
            }
          : null,
      };
    });

    const merged = [...mappedCatalog, ...filteredOrg].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );

    return merged;
  },

  // Stats per system
  getSystemStats: async (): Promise<Record<string, { active_subs: number; total_revenue: number }>> => {
    const { data, error } = await supabase
      .from('org_system_subscriptions')
      .select('system_code, status, effective_price_agorot');
    if (error) throw error;
    const stats: Record<string, { active_subs: number; total_revenue: number }> = {};
    (data ?? []).forEach((s: any) => {
      if (!stats[s.system_code]) stats[s.system_code] = { active_subs: 0, total_revenue: 0 };
      if (s.status === 'active') {
        stats[s.system_code].active_subs++;
        stats[s.system_code].total_revenue += typeof s.effective_price_agorot === 'number'
          ? s.effective_price_agorot
          : 0;
      }
    });
    return stats;
  },

  /**
   * Read the jumpstart subscription for an org, preferring system_subscriptions
   * (catalog source-of-truth per docs/LICENSING.md) and falling back to
   * org_system_subscriptions.
   */
  getOrgJumpstartSubscription: async (
    orgId: string,
  ): Promise<{
    plan_code: string;
    status: string;
    billing_cycle: string | null;
    expires_at: string | null;
  } | null> => {
    const { data: catalog, error: catErr } = await supabase
      .from('system_subscriptions')
      .select('plan_code, status, billing_cycle_months, current_period_end')
      .eq('org_id', orgId)
      .eq('system_code', 'jumpstart')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (catErr) console.warn('[licensing] system_subscriptions:', catErr.message);

    if (catalog) {
      const months = catalog.billing_cycle_months ?? null;
      const billing_cycle =
        months === null ? null : months >= 12 ? 'yearly' : months >= 1 ? 'monthly' : 'none';
      return {
        plan_code: catalog.plan_code,
        status: catalog.status,
        billing_cycle,
        expires_at: catalog.current_period_end ?? null,
      };
    }

    const { data: legacy, error: legacyErr } = await supabase
      .from('org_system_subscriptions')
      .select('plan_code, status, billing_cycle, expires_at')
      .eq('organization_id', orgId)
      .eq('system_code', 'jumpstart')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (legacyErr) throw legacyErr;

    return legacy ?? null;
  },

  getEffectiveLimits: async (orgId: string): Promise<Record<LimitKey, OrgLimitSnapshot>> => {
    const [{ data: limits, error: limitsError }, { data: usage, error: usageError }] = await Promise.all([
      supabase
        .from('org_effective_limits')
        .select('*')
        .eq('org_id', orgId),
      supabase.rpc('org_usage', { p_org_id: orgId }),
    ]);

    if (limitsError) rethrowAsPlatformAdminIfNeeded(limitsError);
    if (usageError) rethrowAsPlatformAdminIfNeeded(usageError);

    if (!(limits as OrgEffectiveLimitRow[] | null | undefined)?.length) {
      throw new PlatformAdminRequiredError();
    }

    return mergeEffectiveLimits(
      limits as OrgEffectiveLimitRow[] | null | undefined,
      usage as OrgUsageRow[] | null | undefined,
    );
  },

  listEffectiveLimitsForOrgs: async (
    orgIds: string[],
  ): Promise<Record<string, Record<LimitKey, OrgLimitSnapshot>>> => {
    if (!orgIds.length) return {};

    const { data: limits, error: limitsError } = await supabase
      .from('org_effective_limits')
      .select('*')
      .in('org_id', orgIds);
    if (limitsError) rethrowAsPlatformAdminIfNeeded(limitsError);

    // After security_invoker, a non–platform-admin JWT gets zero rows for other orgs
    // (RLS filters silently). Surface that as an explicit permission state.
    if (!(limits as OrgEffectiveLimitRow[] | null | undefined)?.length) {
      throw new PlatformAdminRequiredError();
    }

    const usageResults = await Promise.all(
      orgIds.map(async (orgId) => {
        const { data, error } = await supabase.rpc('org_usage', { p_org_id: orgId });
        if (error) rethrowAsPlatformAdminIfNeeded(error);
        return {
          orgId,
          usage: data as OrgUsageRow[] | null | undefined,
        };
      })
    );

    const usageByOrg = new Map(
      usageResults.map((result) => [result.orgId, result.usage])
    );

    return Object.fromEntries(
      orgIds.map((orgId) => {
        const orgLimits = (limits as OrgEffectiveLimitRow[] | null | undefined)?.filter(
          (row) => row.org_id === orgId
        );
        return [orgId, mergeEffectiveLimits(orgLimits, usageByOrg.get(orgId))];
      })
    ) as Record<string, Record<LimitKey, OrgLimitSnapshot>>;
  },
};
